import { generatedCategoryLabels, generatedProjects } from './projects.generated';
import {
  additionalPendingProjects,
  featuredProjectIds,
  featuredProjectOverrides,
  pendingProjectOverrides,
} from './projects.featured';
import type {
  ArchiveLocationGroup,
  ArchiveTypeGroup,
  PortfolioStats,
  ProjectRecord,
  ProjectTypeCategory,
} from './projects.types';

export type {
  ArchiveLocationGroup,
  ArchiveTypeGroup,
  PortfolioStats,
  ProjectDetail,
  ProjectKind,
  ProjectRecord,
  ProjectRecordBase,
  ProjectStatus,
  ProjectTypeCategory,
} from './projects.types';

const LOCATION_ORDER = ['Incline Village, NV', 'Crystal Bay, NV', 'Carson Valley, NV', 'Truckee, CA'] as const;
const TYPE_ORDER: ProjectTypeCategory[] = ['residential', 'commercial', 'hotel', 'mixedUse'];

const LOCATION_DESCRIPTIONS: Record<string, string> = {
  'Incline Village, NV': "Residential, resort-adjacent, office, and retail work across Scott Development's home market.",
  'Crystal Bay, NV': 'Hospitality, entertainment, and mixed-use work along the north shore of Lake Tahoe.',
  'Carson Valley, NV': 'Regional commercial work serving Northern Nevada communities.',
  'Truckee, CA': 'Custom residential work extending the portfolio into the Truckee-Martis Camp market.',
};

function mergeProject(project: ProjectRecord): ProjectRecord {
  if (project.status === 'pending') {
    return { ...project, ...pendingProjectOverrides[project.id] };
  }

  return {
    ...project,
    featured: featuredProjectIds.includes(project.id as (typeof featuredProjectIds)[number]),
    ...featuredProjectOverrides[project.id],
  };
}

export const projects: ProjectRecord[] = generatedProjects.map(mergeProject);

export const allPendingProjects: ProjectRecord[] = [
  ...additionalPendingProjects,
  ...projects.filter((project) => project.status === 'pending'),
];

export function getQozProjects(): ProjectRecord[] {
  return allPendingProjects.filter((project) => project.kind === 'qoz');
}

export function getPendingProjects(): ProjectRecord[] {
  return allPendingProjects.filter((project) => project.kind !== 'qoz');
}

export function getFeaturedCompleted(): ProjectRecord[] {
  return featuredProjectIds
    .map((id) => projects.find((project) => project.id === id))
    .filter((project): project is ProjectRecord => Boolean(project));
}

export function getArchiveProjects(): ProjectRecord[] {
  return projects.filter((project) => project.status === 'completed');
}

export function getPortfolioStats(): PortfolioStats {
  return generatedProjects.reduce(
    (stats, project) => ({
      completedProjects: stats.completedProjects + (project.status === 'completed' ? 1 : 0),
      pendingProjects: stats.pendingProjects + (project.status === 'pending' ? 1 : 0),
      totalSquareFeet: stats.totalSquareFeet + (project.squareFeet ?? 0),
      totalUnits: stats.totalUnits + (project.units ?? 0),
    }),
    {
      completedProjects: 0,
      pendingProjects: additionalPendingProjects.length,
      totalSquareFeet: 0,
      totalUnits: 0,
    },
  );
}

function getLocationId(location: string): string {
  return location
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function groupByType(projectsForLocation: ProjectRecord[]): ArchiveTypeGroup[] {
  return TYPE_ORDER.map((category) => ({
    category,
    label: generatedCategoryLabels[category],
    projects: projectsForLocation.filter((project) => project.typeCategory === category),
  })).filter((group) => group.projects.length > 0);
}

export function getArchiveByLocation(): ArchiveLocationGroup[] {
  const archiveProjects = getArchiveProjects();

  return LOCATION_ORDER.map((location) => {
    const projectsForLocation = archiveProjects.filter((project) => project.location === location);

    return {
      id: getLocationId(location),
      location,
      completedCount: projectsForLocation.length,
      description: LOCATION_DESCRIPTIONS[location],
      groups: groupByType(projectsForLocation),
    };
  }).filter((locationGroup) => locationGroup.completedCount > 0);
}
